import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  CircularProgressbar,
  buildStyles
} from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  ComposedChart,
  AreaChart,
  Area
} from 'recharts';
import { Dropdown, DropdownButton, ButtonGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { getAdjustedStartDate, getAdjustedEndDate } from '../../utils/dateHelper';
import { displayConvertedAmount, getCurrencySettings, convertAmount, formatCurrency as formatCurrencyUtil } from '../../utils/currencyConverter';

const SalesDashboard = () => {
  const navigate = useNavigate();

  const getFinancialYear = () => {
    const monthMap = {
      January: '01', February: '02', March: '03', April: '04',
      May: '05', June: '06', July: '07', August: '08',
      September: '09', October: '10', November: '11', December: '12'
    };

    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonthNum = today.getMonth() + 1;

    try {
      const storedFormData = JSON.parse(localStorage.getItem('formData'));
      
      // 1. Try to get explicit months from settings
      let sMonthName = storedFormData?.start_month;
      let eMonthName = storedFormData?.end_month;

      // 2. Fallback to parsing the financial_year string if months aren't set
      if (!sMonthName || !eMonthName) {
        const fy = storedFormData?.financial_year;
        if (fy && fy.includes('-')) {
          const parts = fy.split('-').map(m => m.trim());
          if (isNaN(parseInt(parts[0]))) {
            sMonthName = parts[0];
            eMonthName = parts[1];
          } else {
            // Case: "2024-2025"
            return {
              startDate: `${parts[0]}-04-01`,
              endDate: `${parts[1]}-03-31`
            };
          }
        }
      }

      if (sMonthName && eMonthName) {
        const sMonth = monthMap[sMonthName];
        const eMonth = monthMap[eMonthName];

        if (sMonth && eMonth) {
          const sMonthInt = parseInt(sMonth);
          // If current month is before start month, started last year
          const startYear = currentMonthNum < sMonthInt ? currentYear - 1 : currentYear;
          const endYear = parseInt(eMonth) < sMonthInt ? startYear + 1 : startYear;
          const endDay = new Date(endYear, parseInt(eMonth), 0).getDate();

          return {
            startDate: `${startYear}-${sMonth}-01`,
            endDate: `${endYear}-${eMonth}-${endDay}`
          };
        }
      }
    } catch (e) {
      console.error("Failed to parse financial year, using fallback:", e);
    }

    // Default Fallback
    return {
      startDate: `${currentYear}-04-01`,
      endDate: `${currentYear + 1}-03-31`
    };
  };
  const [sale, setSale] = useState({});
  const [chartConfig, setChartConfig] = useState({
    year: new Date().getFullYear(),
    months: 6
  });
  const [selectedFilter, setSelectedFilter] = useState("financialYear");
  const [isDateFilterVisible, setIsDateFilterVisible] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [salesUsers, setSalesUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userData, setUserData] = useState(null);
  const [reminders, setReminders] = useState([]);

  // Reactive currency state — re-reads localStorage whenever user changes currency
  const [viewCurrency, setViewCurrency] = useState(() => getCurrencySettings().viewCurrency);

  const COLORS = ['#1e3a8a', '#1d4ed8', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe'];

  const [dashboardStartDate, setDashboardStartDate] = useState(() => getFinancialYear().startDate);
  const [dashboardEndDate, setDashboardEndDate] = useState(() => getFinancialYear().endDate);

  const fetchSale = async (sDate, eDate, userId = selectedUserId) => {
    setIsLoading(true);
    // If no dates provided, fallback to current state or FY
    const finalStart = sDate || dashboardStartDate;
    const finalEnd = eDate || dashboardEndDate;

    setDashboardStartDate(finalStart);
    setDashboardEndDate(finalEnd);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const userInfo = JSON.parse(localStorage.getItem('user-info'));
      const origin = localStorage.getItem('user-origin');
      const token = userInfo?.token;

      const response = await axios.get(`${backendUrl}/api/dashboard/sale`, {
        params: {
          start_date: finalStart,
          end_date: finalEnd,
          created_by: userId || undefined
        },
        headers: {
          Authorization: `Bearer ${token}`,
          'Tenant-Origin': origin,
        },
      });

      setSale(response.data);
      if (response.data?.sales) {
          console.log("=== Sales Dashboard Progress Calculation ===");
          console.log("Total Sales (AED):", response.data.sales.total);
          console.log("Total Target (AED):", response.data.sales.total_target);
          console.log("Target Progress:", response.data.sales.target_progress, "%");
          console.log("Calculation: (" + response.data.sales.total + " / " + response.data.sales.total_target + ") * 100");
          console.log("==========================================");
      }
    } catch (error) {
      console.error("Error fetching data:", error.response?.data || error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReminders = async (userId = selectedUserId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const userInfo = JSON.parse(localStorage.getItem('user-info'));
      const origin = localStorage.getItem('user-origin');
      const token = userInfo?.token;

      const response = await axios.get(`${backendUrl}/api/activities/reminders`, {
        params: { user_id: userId || undefined },
        headers: {
          Authorization: `Bearer ${token}`,
          'Tenant-Origin': origin,
        },
      });
      setReminders(response.data.data);
    } catch (error) {
      console.error("Error fetching reminders:", error);
    }
  };

  const fetchSalesUsers = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const userInfo = JSON.parse(localStorage.getItem('user-info'));
      const origin = localStorage.getItem('user-origin');
      const token = userInfo?.token;

      const response = await axios.get(`${backendUrl}/api/users`, {
        params: { 
          department: 'Sales',
          exclude_parent_null: true
        },
        headers: {
          Authorization: `Bearer ${token}`,
          'Tenant-Origin': origin,
        },
      });
      const data = response.data;
      setSalesUsers(Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : (Array.isArray(data?.users) ? data.users : [])));
    } catch (error) {
      console.error("Error fetching sales users:", error);
    }
  };

  const handleFinancialYearChange = (selectedYear) => {
    if (selectedYear === "financialYear") {
      setSelectedFilter("financialYear");
      setIsDateFilterVisible(false);
      const { startDate, endDate } = getFinancialYear();
      fetchSale(startDate, endDate);
    } else {
      setSelectedFilter("customRange");
      setIsDateFilterVisible(true);
      setCustomDateRange({ startDate: '', endDate: '' });
    }
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setCustomDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClearDates = () => {
    setCustomDateRange({ startDate: '', endDate: '' });
    setIsDateFilterVisible(false);
    fetchSale(null, null);
  };



  const handleSubmit = () => {
    const { startDate, endDate } = customDateRange;
    if (startDate && endDate) {
      fetchSale(startDate, endDate);
      setIsDateFilterVisible(false);
    }
  };

  useEffect(() => {
    const userInfo = JSON.parse(localStorage.getItem('user-info'));
    setUserData(userInfo);

    const roles = userInfo?.user?.roles || [];
    const dataScopes = userInfo?.data_scopes || [];

    const hasAllScope = roles.some(r => (typeof r === 'string' ? r : r?.name || '').toLowerCase() === 'superadmin') || 
                       dataScopes.includes('all') || 
                       dataScopes.includes('team');

    if (hasAllScope) {
      fetchSalesUsers();
    }
    fetchSale();
    fetchReminders();
  }, []);

  // Listen for currency changes from localStorage (cross-tab via 'storage' event,
  // same-tab via custom 'currencyChanged' event dispatched by Profile.jsx save)
  useEffect(() => {
    const handleCurrencyChange = () => {
      const { viewCurrency: newCurrency } = getCurrencySettings();
      setViewCurrency(newCurrency);
    };

    window.addEventListener('storage', handleCurrencyChange);
    window.addEventListener('currencyChanged', handleCurrencyChange);

    return () => {
      window.removeEventListener('storage', handleCurrencyChange);
      window.removeEventListener('currencyChanged', handleCurrencyChange);
    };
  }, []);

  const handleUserChange = (userId) => {
    setSelectedUserId(userId);
    const { startDate, endDate } = customDateRange;
    fetchSale(startDate || null, endDate || null, userId);
    fetchReminders(userId);
  };

  // Reactive formatters — depend on viewCurrency state so they re-render on currency change
  // Both total sales and total_target are in AED (system base currency)
  const formatCurrency = (value) => {
    return formatCurrencyUtil(value);
  };

  const formatUSCurrency = (amount) => {
    return formatCurrencyUtil(amount);
  };

  // Safe progress: capped at 100, no decimals
  const safeProgress = Math.min(100, Math.floor(sale.sales?.target_progress || 0));

  const handleChartConfigChange = (type, value) => {
    const newConfig = { ...chartConfig, [type]: value };
    setChartConfig(newConfig);

    let start, end;
    if (type === 'year') {
      start = dayjs(`${value}-01-01`).format('YYYY-MM-DD');
      end = dayjs(`${value}-12-31`).format('YYYY-MM-DD');
    } else {
      // For months duration, we go back from the current month
      end = dayjs().endOf('month').format('YYYY-MM-DD');
      start = dayjs().subtract(value - 1, 'month').startOf('month').format('YYYY-MM-DD');
    }
    
    fetchSale(start, end);
    setSelectedFilter("customRange");
  };

  const sortedPerformance = (() => {
    const rawData = sale.sales?.monthly_performance || [];
    const dataMap = new Map(rawData.map(item => [item.month, item]));
    const result = [];
    
    // Determine the start date and number of months to show
    let startDate;
    let iterations = chartConfig.months;

    if (chartConfig.year && chartConfig.year !== new Date().getFullYear()) {
      // If a specific past year is selected, show Jan-Dec of that year
      startDate = dayjs(`${chartConfig.year}-01-01`);
      iterations = 12;
    } else {
      // Show last N months relative to now
      startDate = dayjs().subtract(chartConfig.months - 1, 'month').startOf('month');
    }

    for (let i = 0; i < iterations; i++) {
      const monthStr = startDate.add(i, 'month').format('MMM YYYY');
      result.push(dataMap.get(monthStr) || { 
        month: monthStr, 
        orders: 0, 
        value: 0,
        stages: { suspect: 0, prospect: 0, negotiation: 0, order: 0, "closed won": 0, "closed lost": 0 }
      });
    }
    return result;
  })();

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0 font-size-18">
                  Dashboard
                </h4>
              </div>
            </div>
          </div>



          <div className="dashboard-container">
            <div>
              <div>
                <div className="d-flex justify-content-end align-items-center mb-3">

                  {(userData?.roles?.some(r => r.toLowerCase() === 'superadmin') || 
                    userData?.data_scopes?.includes('all') || 
                    userData?.data_scopes?.includes('team')) && (
                    <div className="me-3">
                      <select
                        className="form-select"
                        value={selectedUserId}
                        onChange={(e) => handleUserChange(e.target.value)}
                        style={{ width: '200px' }}
                      >
                        <option value="">All Sales Users</option>
                        {(salesUsers || []).map(user => (
                          <option key={user.id} value={user.id}>{user.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <DropdownButton
                    id="financial-year-dropdown"
                    title={selectedFilter === "financialYear" ? 'This Financial Year' : 'Custom Range'}
                    onSelect={handleFinancialYearChange}
                    variant="outline-primary"
                    className="filter-dropdown"
                  >
                    <Dropdown.Item eventKey="financialYear">This Financial Year</Dropdown.Item>
                    <Dropdown.Item eventKey="custom">Custom Range</Dropdown.Item>
                  </DropdownButton>

                  {isDateFilterVisible && (
                    <div className="date-range-selector d-flex align-items-center gap-2">
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          views={["year", "month", "day"]}
                          openTo="year"
                          format="DD-MM-YYYY"
                          value={customDateRange.startDate ? dayjs(customDateRange.startDate) : null}
                          onChange={(newValue) => {
                            if (newValue) {
                              const currentStartDate = customDateRange.startDate;
                              if (!currentStartDate || dayjs(currentStartDate).year() !== newValue.year()) {
                                const start = newValue.startOf('year');
                                const end = newValue.endOf('year');
                                setCustomDateRange(prev => ({
                                  ...prev,
                                  startDate: start.format('YYYY-MM-DD'),
                                  endDate: end.format('YYYY-MM-DD')
                                }));
                              } else {
                                const { startDate: newStart, suggestedEndDate } = getAdjustedStartDate(newValue, customDateRange.endDate);
                                setCustomDateRange(prev => ({
                                  ...prev,
                                  startDate: newStart,
                                  endDate: suggestedEndDate || prev.endDate
                                }));
                              }
                            }
                          }}
                          slotProps={{ textField: { size: "small", sx: { width: '160px', background: 'white' } } }}
                        />
                      </LocalizationProvider>
                      <span className="date-separator">to</span>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          views={["year", "month", "day"]}
                          openTo="year"
                          format="DD-MM-YYYY"
                          value={customDateRange.endDate ? dayjs(customDateRange.endDate) : null}
                          onChange={(newValue) => {
                            if (newValue) {
                              const currentEndDate = customDateRange.endDate;
                              if (!currentEndDate || dayjs(currentEndDate).year() !== newValue.year()) {
                                const end = newValue.endOf('year');
                                setCustomDateRange(prev => ({
                                  ...prev,
                                  endDate: end.format('YYYY-MM-DD')
                                }));
                              } else {
                                setCustomDateRange(prev => ({
                                  ...prev,
                                  endDate: getAdjustedEndDate(newValue, customDateRange.startDate)
                                }));
                              }
                            }
                          }}
                          slotProps={{ textField: { size: "small", sx: { width: '160px', background: 'white' } } }}
                        />
                      </LocalizationProvider>
                      <button
                        onClick={handleClearDates}
                        className="btn btn-outline-secondary"
                      >
                        Clear
                      </button>
                      <button
                        onClick={handleSubmit}
                        className="btn btn-primary"
                        disabled={!customDateRange.startDate || !customDateRange.endDate}
                      >
                        Apply
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <>
              {/* Key Metrics Section */}
              {/* <div className="metrics-grid1">
            <div className="metric-card1 primary">
              <div className="metric-content1">
                <h3>Total Sales</h3>
                <p className='font-size-18'>{sale.sales?.total}</p>
              </div>
              <div className="metric-icon">
                <i className="bx bx-trending-up"></i>
              </div>
            </div>

            <div className="metric-card1 success">
              <div className="metric-content1">
                <h3>Target Progress</h3>
                <div className="progress-circle">
                  <CircularProgressbar
                    value={sale.sales?.target_progress || 0}
                    text={`${sale.sales?.target_progress || 0}%`}
                    strokeWidth={10}
                    styles={buildStyles({
                      textSize: "16px",
                      pathColor: "#10B981",
                      textColor: "#111827",
                      trailColor: "#E5E7EB",
                    })}
                  />
                </div>
              </div>
            </div>

            <div className="metric-card1 info">
              <div className="metric-content1">
                <h3>Conversion Rate</h3>
                <p>{sale.sales?.conversion_rate || 0}%</p>
              </div>
              <div className="metric-icon">
                <i className="bx bx-transfer"></i>
              </div>
            </div>

            <div className="metric-card1 warning">
              <div className="metric-content1">
                <h3>Avg Order Value</h3>
                <p>{sale.sales?.avg_order_value}</p>
              </div>
              <div className="metric-icon">
                <i className="bx bx-cart"></i>
              </div>
            </div>
          </div> */}
              <div className="metrics-grid3 mb-4">
                {/* Total Sales Card */}
                <div className="metric-card3 sales-card shadow-sm border-0">
                  <div className="card-content">
                    <div className="metric-info">
                      <span className="metric-label text-muted fw-medium">Total Sales</span>
                      <h2 className="metric-value mb-0 fw-bold">{formatUSCurrency(sale.sales?.total)}</h2>
                    </div>
                    <div className="metric-icon rounded-circle bg-primary bg-opacity-10 p-3 text-primary">
                      <i className="bx bx-trending-up fs-3"></i>
                    </div>
                  </div>
                  <div className="metric-footer mt-3 pt-2 border-top">
                    <span className="trend positive text-success small">
                      <i className="bx bx-up-arrow-alt"></i> 12.5% <span className="text-muted">from last period</span>
                    </span>
                  </div>
                </div>

                {/* Target Progress Card */}
                <div className="metric-card3 target-card shadow-sm border-0">
                  <div className="card-content">
                    <div className="metric-info w-100">
                      <span className="metric-label text-muted fw-medium">Target Progress</span>
                      <div className="d-flex align-items-center mt-2">
                        <div className="progress-container" style={{ width: '50px', height: '50px' }}>
                          <CircularProgressbar
                            value={safeProgress}
                            text={`${safeProgress}%`}
                            strokeWidth={12}
                            styles={buildStyles({
                              pathColor: safeProgress >= 100 ? "#10B981" : "#4CAF50",
                              textColor: "#2E7D32",
                              trailColor: "#E8F5E9",
                              textSize: '26px'
                            })}
                          />
                        </div>
                        <div className="ms-3">
                          <div className="text-muted small mb-1">Total Target</div>
                          {/* total_target is in AED (system base currency) */}
                          <div className="fw-bold fs-6">{formatUSCurrency(sale.sales?.total_target || 0)}</div>
                          <div className="text-muted" style={{ fontSize: '11px' }}>
                            Achieved: {formatUSCurrency(sale.sales?.total || 0)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Conversion Rate Card */}
                <div className="metric-card3 conversion-card shadow-sm border-0">
                  <div className="card-content">
                    <div className="metric-info">
                      <span className="metric-label text-muted fw-medium">Conversion Rate</span>
                      <h2 className="metric-value mb-0 fw-bold">{sale.sales?.conversion_rate || 0}%</h2>
                    </div>
                    <div className="metric-icon rounded-circle bg-info bg-opacity-10 p-3 text-info">
                      <i className="bx bx-transfer fs-3"></i>
                    </div>
                  </div>
                  <div className="metric-footer mt-3 pt-2 border-top">
                    <span className="trend positive text-success small">
                      <i className="bx bx-up-arrow-alt"></i> 3.2% <span className="text-muted">from last month</span>
                    </span>
                  </div>
                </div>

                {/* Average Order Value Card */}
                <div className="metric-card3 aov-card shadow-sm border-0">
                  <div className="card-content">
                    <div className="metric-info">
                      <span className="metric-label text-muted fw-medium">Avg Order Value</span>
                      <h2 className="metric-value mb-0 fw-bold">{formatUSCurrency(sale.sales?.avg_order_value)}</h2>
                    </div>
                    <div className="metric-icon rounded-circle bg-warning bg-opacity-10 p-3 text-warning">
                      <i className="bx bx-cart fs-3"></i>
                    </div>
                  </div>
                  <div className="metric-footer mt-3 pt-2 border-top">
                    <span className="trend neutral text-secondary small">
                      <i className="bx bx-minus"></i> 0.5% <span className="text-muted">from last month</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Monthly Performance Trends */}
              <div className="row mb-4">
                <div className="col-lg-8">
                  <div className="card shadow-sm border-0 h-100" style={{ borderRadius: '15px' }}>
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <div className="d-flex align-items-center gap-3">
                          <h4 className="card-title mb-0 font-size-16 fw-bold">Monthly Orders & Revenue</h4>
                          <div className="d-flex gap-2">
                            <select 
                              className="form-select form-select-sm" 
                              style={{ width: '100px', fontSize: '12px' }}
                              value={chartConfig.year}
                              onChange={(e) => handleChartConfigChange('year', parseInt(e.target.value))}
                            >
                              {[0, 1, 2, 3, 4, 5].map(offset => {
                                const y = new Date().getFullYear() - offset;
                                return <option key={y} value={y}>{y}</option>;
                              })}
                            </select>
                            <select 
                              className="form-select form-select-sm" 
                              style={{ width: '110px', fontSize: '12px' }}
                              value={chartConfig.months}
                              onChange={(e) => handleChartConfigChange('months', parseInt(e.target.value))}
                            >
                              <option value={6}>Last 6 Months</option>
                              <option value={12}>Last 12 Months</option>
                              <option value={24}>Last 24 Months</option>
                            </select>
                          </div>
                        </div>
                        <div className="d-flex gap-3 small">
                          <span className="d-flex align-items-center"><span className="rounded-circle bg-primary me-1" style={{ width: '10px', height: '10px' }}></span> Orders Volume</span>
                          <span className="d-flex align-items-center"><span className="rounded-circle bg-warning me-1" style={{ width: '10px', height: '10px' }}></span> Revenue Value</span>
                        </div>
                      </div>
                      <div style={{ height: '350px', width: '100%' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart 
                            data={sortedPerformance}
                            style={{ cursor: 'pointer' }}
                            onClick={(data) => {
                              if (data && data.activeLabel) {
                                navigate('/deal', { state: { filterMonth: data.activeLabel } });
                              }
                            }}
                          >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                            <XAxis 
                              dataKey="month" 
                              axisLine={false} 
                              tickLine={false} 
                              tick={{ fill: '#6B7280', fontSize: 12 }}
                              dy={10}
                            />
                            <YAxis 
                              yAxisId="left"
                              axisLine={false} 
                              tickLine={false} 
                              tick={{ fill: '#6B7280', fontSize: 12 }}
                            />
                            <YAxis 
                              yAxisId="right" 
                              orientation="right" 
                              axisLine={false} 
                              tickLine={false} 
                              tick={{ fill: '#6B7280', fontSize: 12 }}
                              tickFormatter={(value) => `${(value/1000).toFixed(0)}k`}
                            />
                            <Tooltip 
                              contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                              cursor={{ fill: '#f1f5f9' }}
                            />
                            <Legend wrapperStyle={{ paddingTop: '20px' }} />
                            <Bar 
                              yAxisId="left" 
                              dataKey="orders" 
                              name="Orders Count" 
                              fill="#559dfc" 
                              radius={[4, 4, 0, 0]} 
                              barSize={35} 
                            />
                            <Line 
                              yAxisId="right" 
                              type="monotone" 
                              dataKey="value" 
                              name="Revenue Value" 
                              stroke="#F59E0B" 
                              strokeWidth={3} 
                              dot={{ r: 4, fill: '#F59E0B' }} 
                              activeDot={{ r: 6 }}
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-4">
                  <div className="card shadow-sm border-0 h-100" style={{ borderRadius: '15px' }}>
                    <div className="card-body">
                      <h4 className="card-title mb-4 font-size-16 fw-bold">Orders Volume</h4>
                      <div className="d-flex align-items-center justify-content-center" style={{ height: '300px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart 
                            data={sortedPerformance}
                            style={{ cursor: 'pointer' }}
                            onClick={(data) => {
                              if (data && data.activeLabel) {
                                navigate('/deal', { state: { filterMonth: data.activeLabel } });
                              }
                            }}
                          >
                            <defs>
                              <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                              </linearGradient>
                            </defs>
                            <XAxis dataKey="month" hide />
                            <Tooltip />
                            <Area 
                              type="monotone" 
                              dataKey="orders" 
                              stroke="#8b5cf6" 
                              fillOpacity={1} 
                              fill="url(#colorOrders)" 
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="text-center mt-3">
                        <h3 className="mb-1 fw-bold">{sale.sales?.monthly_performance?.reduce((acc, curr) => acc + curr.orders, 0) || 0}</h3>
                        <p className="text-muted small mb-0">Total Orders in Period</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sales Pipeline Sections */}
              <div className="row mb-4 mt-2">
                <div className="col-md-6">
                  <div className="card shadow-sm border-0" style={{ borderRadius: '15px', height: '100%', minHeight: '450px' }}>
                    <div className="card-body">
                      <h4 className="card-title mb-4 font-size-16 fw-bold">Value-Based Funnel</h4>
                      <div style={{ width: '100%', padding: '4px 0', display: 'flex', justifyContent: 'center' }}>
                        <div style={{ width: '75%' }}>
                        {(() => {
                          const allStages = ["suspect", "prospect", "approach", "negotiation", "closed won", "order", "closed dead", "closed lost"];
                          const stageColorMap = {
                            suspect:       { body: '#EAF4FF', rim: '#ffffff', text: '#1a3a6e' },
                            prospect:      { body: '#B7D9FF', rim: '#D6EAFF', text: '#1a3a6e' },
                            approach:      { body: '#8FC2FF', rim: '#B7D9FF', text: '#1a3a6e' },
                            negotiation:   { body: '#5FA6FF', rim: '#8FC2FF', text: '#fff' },
                            'closed won':  { body: '#2F7EF7', rim: '#5FA6FF', text: '#fff' },
                            order:         { body: '#1F5FD6', rim: '#2F7EF7', text: '#fff' },
                            'closed dead': { body: '#163E8F', rim: '#1F5FD6', text: '#fff' },
                            'closed lost': { body: '#0d2b6e', rim: '#163E8F', text: '#fff' },
                          };

                          const stageData = allStages.map(s => {
                            const apiData = sale?.sales?.by_stage?.find(x => x.stage?.toLowerCase() === s.toLowerCase());
                            return { stage: s, amount: apiData ? apiData.amount : 0 };
                          });

                          const N = stageData.length;
                          const svgW = 280;
                          const sliceH = 26;
                          const rimRY = 5;
                          const maxHW = svgW / 2;
                          const minHW = 30;
                          const cx = svgW / 2;

                          const hw = (i) => maxHW - (maxHW - minHW) * i / N;

                          // Total SVG height: N slices + one top rim
                          const totalH = N * sliceH + rimRY;

                          // Build segments from bottom up so earlier (wider) stages render on top
                          const segments = [...stageData].map((stage, i) => ({
                            ...stage, index: i,
                            topHW: hw(i), botHW: hw(i + 1),
                            topY: i * sliceH + rimRY,
                            botY: i * sliceH + rimRY + sliceH,
                            color: stageColorMap[stage.stage] || { body: '#5FA6FF', rim: '#8FC2FF', text: '#fff' }
                          })).reverse(); // render bottom first

                          return (
                            <svg viewBox={`0 0 ${svgW} ${totalH}`} width="100%" style={{ display: 'block', overflow: 'visible' }}>
                              <defs>
                                {stageData.map((stage, i) => (
                                  <linearGradient key={i} id={`fg${i}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={stageColorMap[stage.stage]?.rim || '#B7D9FF'} stopOpacity="1" />
                                    <stop offset="100%" stopColor={stageColorMap[stage.stage]?.body || '#5FA6FF'} stopOpacity="1" />
                                  </linearGradient>
                                ))}
                              </defs>

                              {segments.map(({ stage, amount, index: i, topHW, botHW, topY, botY, color }) => {
                                const midY = (topY + botY) / 2;
                                // Trapezoid: top edge arc then straight sides then bottom arc
                                const bodyPath = [
                                  `M ${cx - topHW} ${topY}`,
                                  `A ${topHW} ${rimRY} 0 0 1 ${cx + topHW} ${topY}`,  // top arc (visible rim)
                                  `L ${cx + botHW} ${botY}`,
                                  `A ${botHW} ${rimRY} 0 0 1 ${cx - botHW} ${botY}`,  // bottom arc
                                  `Z`
                                ].join(' ');

                                const textX = cx - topHW + 10 + (topHW - botHW) * 0.35;
                                const amountX = cx + topHW - 10 - (topHW - botHW) * 0.35;

                                return (
                                  <g 
                                    key={stage} 
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => navigate('/deal', { state: { filterStage: stage } })}
                                  >
                                    {/* Main body - gradient fill trapezoid */}
                                    <path d={bodyPath} fill={`url(#fg${i})`} stroke="rgba(255,255,255,0.5)" strokeWidth="1" />
                                    {/* Bottom inner ellipse - darker, gives depth */}
                                    <ellipse cx={cx} cy={botY} rx={botHW} ry={rimRY}
                                      fill={color.body}
                                      opacity="0.7"
                                    />
                                    {/* Top rim ellipse - lighter color, gives 3D opening effect */}
                                    <ellipse cx={cx} cy={topY} rx={topHW} ry={rimRY}
                                      fill={color.rim}
                                      stroke="rgba(255,255,255,0.6)" strokeWidth="1"
                                    />
                                    {/* Stage label */}
                                    <text x={textX} y={midY} dominantBaseline="middle"
                                      fill={color.text} fontSize="5" fontWeight="600"
                                      style={{ textTransform: 'capitalize' }}
                                    >
                                      {stage.charAt(0).toUpperCase() + stage.slice(1)}
                                    </text>
                                    <text x={amountX} y={midY} dominantBaseline="middle" textAnchor="end"
                                      fill={color.text} fontSize="5" fontWeight="500"
                                    >
                                      {formatCurrency(amount)}
                                    </text>
                                  </g>
                                );
                              })}
                            </svg>
                          );
                        })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card shadow-sm border-0" style={{ borderRadius: '15px', height: '100%', minHeight: '450px' }}>
                    <div className="card-body">
                      <div className="d-flex flex-column mb-4">
                        <h4 className="card-title mb-1 font-size-18 fw-bold text-primary">Deal Flow Dynamics & Velocity</h4>
                        <p className="text-muted small mb-0">Tracking the volume and progression of opportunities through each sales milestone.</p>
                      </div>
                      <div style={{ height: '350px', width: '100%' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart 
                            data={sortedPerformance} 
                            barGap={8}
                            style={{ cursor: 'pointer' }}
                            onClick={(data) => {
                              if (data && data.activeLabel) {
                                navigate('/deal', { state: { filterMonth: data.activeLabel } });
                              }
                            }}
                          >
                            <defs>
                              <linearGradient id="gradSuspect" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#E0F2FE" stopOpacity={1}/>
                                <stop offset="100%" stopColor="#bae6fd" stopOpacity={1}/>
                              </linearGradient>
                              <linearGradient id="gradProspect" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#bae6fd" stopOpacity={1}/>
                                <stop offset="100%" stopColor="#7dd3fc" stopOpacity={1}/>
                              </linearGradient>
                              <linearGradient id="gradNegotiation" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#7dd3fc" stopOpacity={1}/>
                                <stop offset="100%" stopColor="#38bdf8" stopOpacity={1}/>
                              </linearGradient>
                              <linearGradient id="gradOrder" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#38bdf8" stopOpacity={1}/>
                                <stop offset="100%" stopColor="#0ea5e9" stopOpacity={1}/>
                              </linearGradient>
                              <linearGradient id="gradWon" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#0ea5e9" stopOpacity={1}/>
                                <stop offset="100%" stopColor="#0284c7" stopOpacity={1}/>
                              </linearGradient>
                              <linearGradient id="gradLost" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#94a3b8" stopOpacity={1}/>
                                <stop offset="100%" stopColor="#64748b" stopOpacity={1}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                            <XAxis 
                              dataKey="month" 
                              axisLine={false} 
                              tickLine={false} 
                              tick={{ fill: '#6B7280', fontSize: 11, fontWeight: 500 }} 
                              dy={10}
                            />
                            <YAxis 
                              axisLine={false} 
                              tickLine={false} 
                              tick={{ fill: '#6B7280', fontSize: 11 }} 
                            />
                            <Tooltip 
                              cursor={{fill: '#f8fafc'}}
                              contentStyle={{ 
                                borderRadius: '12px', 
                                border: 'none', 
                                boxDirection: 'column',
                                boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                                padding: '12px'
                              }} 
                            />
                            <Legend 
                              iconType="circle" 
                              wrapperStyle={{ fontSize: '12px', paddingTop: '25px', fontWeight: 500 }} 
                            />
                            <Bar 
                              dataKey="stages.suspect" 
                              name="Suspect" 
                              stackId="a" 
                              fill="url(#gradSuspect)" 
                              radius={[0, 0, 0, 0]} 
                              barSize={40} 
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate('/deal', { state: { filterStage: 'suspect', startDate: dashboardStartDate, endDate: dashboardEndDate, viewCurrency } })}
                            />
                            <Bar 
                              dataKey="stages.prospect" 
                              name="Prospect" 
                              stackId="a" 
                              fill="url(#gradProspect)" 
                              radius={[0, 0, 0, 0]} 
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate('/deal', { state: { filterStage: 'prospect', startDate: dashboardStartDate, endDate: dashboardEndDate, viewCurrency } })}
                            />
                            <Bar 
                              dataKey="stages.negotiation" 
                              name="Negotiation" 
                              stackId="a" 
                              fill="url(#gradNegotiation)" 
                              radius={[0, 0, 0, 0]} 
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate('/deal', { state: { filterStage: 'negotiation', startDate: dashboardStartDate, endDate: dashboardEndDate, viewCurrency } })}
                            />
                            <Bar 
                              dataKey="stages.order" 
                              name="Order" 
                              stackId="a" 
                              fill="url(#gradOrder)" 
                              radius={[0, 0, 0, 0]} 
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate('/deal', { state: { filterStage: 'order', startDate: dashboardStartDate, endDate: dashboardEndDate, viewCurrency } })}
                            />
                            <Bar 
                              dataKey="stages.closed won" 
                              name="Won" 
                              stackId="a" 
                              fill="url(#gradWon)" 
                              radius={[0, 0, 0, 0]} 
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate('/deal', { state: { filterStage: 'closed won', startDate: dashboardStartDate, endDate: dashboardEndDate, viewCurrency } })}
                            />
                            <Bar 
                              dataKey="stages.closed lost" 
                              name="Lost" 
                              stackId="a" 
                              fill="url(#gradLost)" 
                              radius={[4, 4, 0, 0]} 
                              style={{ cursor: 'pointer' }}
                              onClick={() => navigate('/deal', { state: { filterStage: 'closed lost', startDate: dashboardStartDate, endDate: dashboardEndDate, viewCurrency } })}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Activity Reminders Section */}
              {reminders.length > 0 && (
                <div className="dashboard-section mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Upcoming Activity Reminders</h5>
                    <span className="badge bg-soft-primary text-primary">{reminders.length} Pending</span>
                  </div>
                  <div className="row">
                    {reminders.map((reminder) => (
                      <div key={reminder.id} className="col-md-4 mb-3">
                        <div className="card h-100 shadow-sm border-0" style={{ borderRadius: '15px', borderLeft: `5px solid ${reminder.activity_type?.color || '#559dfc'}` }}>
                          <div className="card-body p-3">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <span className="badge" style={{ backgroundColor: `${reminder.activity_type?.color}20`, color: reminder.activity_type?.color || '#559dfc', fontSize: '11px' }}>
                                {reminder.activity_type?.name || reminder.activity_type || 'Activity'}
                              </span>
                              <small className="text-muted">
                                <i className="bx bx-calendar me-1"></i>
                                {new Date(reminder.due_date).toLocaleDateString()}
                              </small>
                            </div>
                            <h6 className="card-title mb-1 text-truncate" title={reminder.subject}>{reminder.subject}</h6>
                            <p className="card-text text-muted small mb-2 text-truncate-2" style={{ height: '32px', overflow: 'hidden' }}>
                              {reminder.notes || 'No description provided'}
                            </p>
                            <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                              <span className={`badge ${reminder.priority === 'high' ? 'bg-danger' : reminder.priority === 'low' ? 'bg-info' : 'bg-warning'}`} style={{ fontSize: '10px' }}>
                                {reminder.priority?.toUpperCase()}
                              </span>
                              {reminder.reminder_at && (
                                <small className="text-danger fw-bold">
                                  <i className="bx bx-alarm me-1"></i>
                                  {new Date(reminder.reminder_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </small>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sales Breakdown Section */}
              <div className="dashboard-section">
                <h5>Sales Breakdown</h5>
                <div className="breakdown-grid">
                  <div className="breakdown-card">
                    <h4>By Product</h4>
                    {sale?.sales?.by_product?.length ? (
                      <div className="chart-container">
                        <ResponsiveContainer width="100%" height={100}>
                          <BarChart
                            data={sale.sales.by_product
                              .map(item => ({ ...item, sales: parseFloat(item.sales) }))
                              .sort((a, b) => b.sales - a.sales)}
                            layout="vertical"
                            margin={{ top: 5, right: 10, bottom: 5, left: 30 }}
                          >
                            <XAxis type="number" hide />
                            <YAxis type="category" dataKey="product" width={100} hide />
                            <Tooltip
                              formatter={(value) => [`₹ ${value.toLocaleString()}`, 'Sales']}
                            />
                            <Bar
                              dataKey="sales"
                              radius={[0, 4, 4, 0]}
                              fill="#8bbafc"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="no-data">No product sales data available</div>
                    )}
                  </div>

                  <div className="breakdown-card shadow-sm border-0 bg-white" style={{ borderRadius: '15px', padding: '20px' }}>
                    <h4 className="font-size-16 fw-bold text-primary mb-3">Revenue by Region</h4>
                    {sale?.sales?.by_region?.length ? (
                      <div className="chart-container">
                        <ResponsiveContainer width="100%" height={280}>
                          <PieChart>
                            <defs>
                              {COLORS.map((color, index) => (
                                <linearGradient key={`grad-${index}`} id={`pieGrad-${index}`} x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor={color} stopOpacity={1} />
                                  <stop offset="100%" stopColor={color} stopOpacity={0.7} />
                                </linearGradient>
                              ))}
                            </defs>
                            <Pie
                              data={sale.sales.by_region}
                              dataKey="sales"
                              nameKey="region"
                              innerRadius={0}
                              outerRadius={110}
                              minAngle={15}
                              stroke="#fff"
                              strokeWidth={2}
                              labelLine={false}
                              style={{ cursor: 'pointer' }}
                              onClick={(data) => {
                                if (data && data.region) {
                                  navigate('/deal', { state: { filterCountry: data.region, startDate: dashboardStartDate, endDate: dashboardEndDate, viewCurrency } });
                                }
                              }}
                              label={({ cx, cy, midAngle, innerRadius, outerRadius, value, index, payload, percent }) => {
                                 const RADIAN = Math.PI / 180;
                                 const radius = innerRadius + (outerRadius - innerRadius) * 0.65;
                                 const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                 const y = cy + radius * Math.sin(-midAngle * RADIAN);
                                 
                                 return (
                                   <text 
                                     x={x} 
                                     y={y} 
                                     fill="white" 
                                     textAnchor="middle" 
                                     dominantBaseline="central"
                                     style={{ fontSize: '10px', fontWeight: 'bold', textShadow: '0px 1px 2px rgba(0,0,0,0.6)' }}
                                   >
                                     <tspan x={x} dy="-0.6em">{payload.region}</tspan>
                                     <tspan x={x} dy="1.2em">{formatUSCurrency(value)}</tspan>
                                   </text>
                                 );
                                }}
                            >
                              {sale.sales.by_region.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={`url(#pieGrad-${index % COLORS.length})`} />
                              ))}
                            </Pie>
                            <Tooltip
                              contentStyle={{ 
                                borderRadius: '12px', 
                                border: 'none', 
                                boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                                padding: '10px'
                              }}
                              formatter={(value) => [formatUSCurrency(value), 'Revenue']}
                            />
                            <Legend verticalAlign="bottom" height={36}/>
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="no-data py-5 text-center text-muted">No regional sales data available</div>
                    )}
                  </div>

                </div>
              </div>

              {/* Additional Metrics Section */}
              <div className="dashboard-section">
                <h5>Performance Metrics</h5>
                <div className="metrics-grid1 secondary">
                  <div className="metric-card1">
                    <h6>New Customers</h6>
                    <p className="large-text font-size-18">{sale.sales?.new_customers || 0}</p>
                    <div className="metric-footer">
                      <span className="trend up">
                        <i className="bx bx-up-arrow-alt"></i> 12%
                      </span>
                      <span>vs last period</span>
                    </div>
                  </div>

                  <div className="metric-card1">
                    <h6>Pending Quotations</h6>
                    <p className="large-text font-size-18">{sale.sales?.pending_documents || 0}</p>
                    <div className="metric-footer">
                      <span className="trend down">
                        <i className="bx bx-down-arrow-alt"></i> 5%
                      </span>
                      <span>vs last period</span>
                    </div>
                  </div>

                  <div className="metric-card1">
                    <h6>Pending Invoices</h6>
                    <p className="large-text font-size-18">{sale.sales?.pending_invoices || 0}</p>
                    <div className="metric-footer">
                      <span className="trend up">
                        <i className="bx bx-up-arrow-alt"></i> 8%
                      </span>
                      <span>vs last period</span>
                    </div>
                  </div>

                  <div className="metric-card1">
                    <h6>Top Salesperson</h6>
                    <p className="large-text font-size-18">
                      {sale.sales?.top_salesperson?.name || ''}
                    </p>
                    <div className="metric-footer">
                      <span>{sale.sales?.top_salesperson?.amount}</span>
                    </div>
                  </div>
                </div>
              </div>
            </>


          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesDashboard;
